"use client"

import { PropsWithChildren, useEffect, useState } from "react"
import { storyblokEditable } from "@storyblok/react/rsc"

import type { CommonPageContext } from "@/lib/tracking/tracking"
import { UbCustomerTypeDependentStoryblok } from "@/components/storyblok/component-types"

interface StorefrontContextResponse extends CommonPageContext {
  oci: boolean
}

interface UbCustomerTypeDependentClientProps extends PropsWithChildren {
  blok: UbCustomerTypeDependentStoryblok
  sbLocale?: string
}

export default function UbCustomerTypeDependentClient({ blok, sbLocale, children }: UbCustomerTypeDependentClientProps) {
  const [storefrontContext, setStorefrontContext] = useState<StorefrontContextResponse | undefined>()

  useEffect(() => {
    if (storefrontContext) {
      return
    }

    const controller = new AbortController()
    const contextUrl = sbLocale
      ? `/api/storefront/context?sbLocale=${encodeURIComponent(sbLocale)}`
      : "/api/storefront/context"

    fetch(contextUrl, {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : undefined))
      .then((context: StorefrontContextResponse | undefined) => {
        if (context) {
          setStorefrontContext(context)
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.error("Could not load storefront context for customer-type-dependent content", error)
        }
      })

    return () => controller.abort()
  }, [sbLocale, storefrontContext])

  const showOnB2b = blok.showOnB2b || false
  const showOnB2c = blok.showOnB2c || false
  const showMyContent =
    storefrontContext && ((storefrontContext.isB2B && showOnB2b) || (!storefrontContext.isB2B && showOnB2c))

  if (!showMyContent) {
    return null
  }

  return <div {...storyblokEditable(blok)}>{children}</div>
}
